import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import {
  AccountTreeOutlined as HierarchyIcon,
  AdminPanelSettingsOutlined as RoleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  PersonAddAlt1Outlined as AddUserIcon,
  SupervisorAccountOutlined as ManagerIcon,
  WarningAmberRounded as WarningIcon,
} from '@mui/icons-material';
import MultiSelectFilter, { SearchFilter, TogglePill } from '../components/monthly/MultiSelectFilter';
import PageHeader, { PageContainer } from '../components/PageHeader';
import { useFillViewportHeight } from '../components/monthly/tableStyles';
import UserPanel, { RoleChip, UserAvatar } from '../components/users/UserPanel';
import { GREEN, GREEN_SOFT, INK, LINE, MUTED } from '../components/home/homeTheme';
import { getRoleDisplayName } from '../utils/roleManager';
import { fetchUsersBundle } from '../utils/usersService';

const PAGE_SIZE = 100;
const EMPTY = [];
const num = (n) => Number(n || 0).toLocaleString('en-GB');
const headSx = { fontSize: 12, fontWeight: 700, color: MUTED, bgcolor: 'var(--rt-paper)', py: 1, borderBottom: `1px solid ${LINE}`, whiteSpace: 'nowrap' };
const cellSx = { fontSize: 13, color: INK, py: 0.9, borderBottom: `1px solid ${LINE}` };

const SORTERS = {
  name: (a, b) => a.username.localeCompare(b.username),
  role: (a, b) => getRoleDisplayName(Number(a.role_id)).localeCompare(getRoleDisplayName(Number(b.role_id))) || SORTERS.name(a, b),
  org: (a, b) => String(a.org_role?.name || '~').localeCompare(String(b.org_role?.name || '~')) || SORTERS.name(a, b),
  manager: (a, b) => String(a.people_manager?.username || '~').localeCompare(String(b.people_manager?.username || '~')) || SORTERS.name(a, b),
};

/**
 * Users V2: elenco utenti con filtri a pillola e pannello laterale (dettaglio, modifica, creazione).
 * L'utente aperto è nell'URL (?user=ID); /users/:id apre direttamente il pannello.
 */
export default function UsersV2() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = routeId || searchParams.get('user');
  const creating = searchParams.get('new') === '1';

  const [refreshToken, setRefreshToken] = useState(0);
  const [data, setData] = useState({ users: EMPTY, roles: EMPTY, peopleRoles: EMPTY });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    fetchUsersBundle()
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(`Unable to load users: ${e.message}`))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [refreshToken]);

  const { users, roles, peopleRoles } = data;

  // ---- Riporti diretti per utente ----
  const reportsCount = useMemo(() => {
    const counts = {};
    users.forEach((u) => {
      if (u.id_people_manager != null) counts[u.id_people_manager] = (counts[u.id_people_manager] || 0) + 1;
    });
    return counts;
  }, [users]);
  const managers = useMemo(() => users.filter((u) => reportsCount[u.id]), [users, reportsCount]);
  const withoutManager = useMemo(() => users.filter((u) => !u.id_people_manager).length, [users]);

  // ---- Filtri ----
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState(EMPTY);
  const [orgFilter, setOrgFilter] = useState(EMPTY);
  const [managerFilter, setManagerFilter] = useState(EMPTY);
  const [onlyWithoutManager, setOnlyWithoutManager] = useState(false);
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const [page, setPage] = useState(0);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = users.filter((u) => {
      if (roleFilter.length && !roleFilter.includes(u.role_id)) return false;
      if (orgFilter.length && !orgFilter.includes(u.org_role_id ?? 'none')) return false;
      if (managerFilter.length && !managerFilter.includes(u.id_people_manager)) return false;
      if (onlyWithoutManager && u.id_people_manager) return false;
      if (!q) return true;
      return [u.username, u.email, String(u.id)].some((v) => String(v || '').toLowerCase().includes(q));
    });
    const sorter = sort.key === 'reports' ? (a, b) => (reportsCount[a.id] || 0) - (reportsCount[b.id] || 0) || SORTERS.name(a, b) : SORTERS[sort.key];
    list.sort(sorter);
    if (sort.dir === 'desc') list.reverse();
    return list;
  }, [users, search, roleFilter, orgFilter, managerFilter, onlyWithoutManager, sort, reportsCount]);

  useEffect(() => setPage(0), [rows.length, sort]);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // ---- Pannello ----
  const openUser = users.find((u) => String(u.id) === String(openId)) || null;
  const showUser = (id) => (routeId ? navigate(`/users?user=${id}`) : setSearchParams({ user: String(id) }));
  const closePanel = () => (routeId ? navigate('/users') : setSearchParams({}));
  const onSaved = (id, message) => {
    setSnack(message);
    setRefreshToken((t) => t + 1);
    if (id) showUser(id);
    else closePanel();
  };

  const tableRef = useRef(null);
  const fillHeight = useFillViewportHeight(tableRef, [loading, error]);

  const sortHeader = (key, label, align) => (
    <TableCell sx={{ ...headSx, textAlign: align }} sortDirection={sort.key === key ? sort.dir : false}>
      <TableSortLabel
        active={sort.key === key}
        direction={sort.key === key ? sort.dir : 'asc'}
        onClick={() => setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }))}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <PageContainer>
      <PageHeader
        title={'Users'}
        subtitle={loading ? 'Loading…' : `${num(users.length)} users · ${num(managers.length)} people managers`}
      >
        <Button
          variant="contained"
          color="success"
          startIcon={<AddUserIcon />}
          onClick={() => setSearchParams({ new: '1' })}
          disabled={loading}
          sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none', height: 40, bgcolor: GREEN }}
        >
          Add user
        </Button>
      </PageHeader>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* Filtri */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <MultiSelectFilter
          label="App roles"
          allLabel="All app roles"
          icon={<RoleIcon />}
          options={roles.map((r) => ({ id: r.id, name: getRoleDisplayName(Number(r.id)) }))}
          value={roleFilter}
          onChange={setRoleFilter}
          width={240}
        />
        <MultiSelectFilter
          label="Hierarchical roles"
          allLabel="All hierarchical roles"
          icon={<HierarchyIcon />}
          options={[...peopleRoles.map((r) => ({ id: r.id, name: r.name })), { id: 'none', name: 'Not assigned' }]}
          value={orgFilter}
          onChange={setOrgFilter}
          width={260}
        />
        <MultiSelectFilter
          label="Managers"
          allLabel="Any manager"
          icon={<ManagerIcon />}
          options={managers.map((m) => ({ id: m.id, name: m.username, caption: `${reportsCount[m.id]} direct report${reportsCount[m.id] === 1 ? '' : 's'}` }))}
          value={managerFilter}
          onChange={setManagerFilter}
          width={240}
        />
        <TogglePill label={`No people manager (${num(withoutManager)})`} checked={onlyWithoutManager} onChange={setOnlyWithoutManager} icon={<WarningIcon />} />
        <SearchFilter value={search} onChange={setSearch} placeholder="Search name, email or ID" width={260} />
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Box ref={tableRef} sx={{ border: `1px solid ${LINE}`, borderRadius: 3, bgcolor: 'var(--rt-paper)', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress color="success" />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: fillHeight ? fillHeight - 48 : 'calc(100vh - 260px)' }}>
              <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', minWidth: 860 }}>
                <colgroup>
                  <col />
                  <col style={{ width: 190 }} />
                  <col style={{ width: 180 }} />
                  <col style={{ width: 260 }} />
                  <col style={{ width: 100 }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    {sortHeader('name', 'Person')}
                    {sortHeader('role', 'App role')}
                    {sortHeader('org', 'Hierarchical role')}
                    {sortHeader('manager', 'People manager')}
                    {sortHeader('reports', 'Reports', 'right')}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.map((u) => {
                    const selected = String(u.id) === String(openId);
                    return (
                      <TableRow
                        key={u.id}
                        hover
                        selected={selected}
                        onClick={() => showUser(u.id)}
                        sx={{ cursor: 'pointer', '&.Mui-selected, &.Mui-selected:hover': { bgcolor: GREEN_SOFT } }}
                      >
                        <TableCell sx={cellSx}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                            <UserAvatar name={u.username} size={32} />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, color: INK }}>
                                {u.username}
                              </Typography>
                              <Typography noWrap sx={{ fontSize: 12, color: MUTED }}>
                                {u.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={cellSx}>
                          <RoleChip roleId={u.role_id} />
                        </TableCell>
                        <TableCell sx={cellSx}>
                          <Typography noWrap sx={{ fontSize: 13, color: u.org_role?.name ? INK : MUTED }}>
                            {u.org_role?.name || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={cellSx}>
                          {u.people_manager ? (
                            <Typography noWrap sx={{ fontSize: 13, color: INK }} title={u.people_manager.email}>
                              {u.people_manager.username}
                            </Typography>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'var(--rt-warn)' }}>
                              <WarningIcon sx={{ fontSize: 16 }} />
                              <Typography sx={{ fontSize: 13, color: 'inherit' }}>Not assigned</Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell sx={{ ...cellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: reportsCount[u.id] ? INK : MUTED }}>
                          {reportsCount[u.id] || '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!pageRows.length && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ ...cellSx, py: 4, textAlign: 'center', color: MUTED }}>
                        No users match these filters.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderTop: `1px solid ${LINE}`, bgcolor: 'var(--rt-subtle-2)' }}>
              <Typography sx={{ fontSize: 13, color: MUTED }}>
                {num(rows.length)} user{rows.length === 1 ? '' : 's'}
                {rows.length !== users.length ? ` of ${num(users.length)}` : ''} · click a row to open it
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: 13, color: MUTED }}>
                  {rows.length ? `${num(page * PAGE_SIZE + 1)}–${num(Math.min(rows.length, (page + 1) * PAGE_SIZE))} of ${num(rows.length)}` : '0 of 0'}
                </Typography>
                <IconButton size="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)} aria-label="Previous page">
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" disabled={page >= pageCount - 1} onClick={() => setPage((p) => p + 1)} aria-label="Next page">
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </>
        )}
      </Box>

      <UserPanel
        open={!!openUser || creating}
        user={creating ? null : openUser}
        creating={creating}
        users={users}
        roles={roles}
        peopleRoles={peopleRoles}
        onClose={closePanel}
        onOpenUser={showUser}
        onSaved={onSaved}
      />

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
      </Box>
    </PageContainer>
  );
}
